import React, { createContext, useContext, useState, useEffect } from "react";
import { FloatingGettingStartedGuide } from "./FloatingGettingStartedGuide";
import { useGettingStartedData } from "../getting-started-guide/shared/useGettingStartedData";
import { shouldShowGettingStarted } from "../getting-started-guide/shared/gettingStartedUtils";

interface FloatingGettingStartedState {
  isVisible: boolean;
  showGuide: () => void;
  hideGuide: () => void;
  dismissGuide: () => void;
}

const FloatingGettingStartedContext = createContext<FloatingGettingStartedState | undefined>(undefined);

const DISMISSED_KEY = "wealthbee-floating-guide-dismissed";

interface FloatingGettingStartedProviderProps {
  children: React.ReactNode;
  /**
   * Routes where the floating guide should NOT appear
   * Useful for excluding pages like login, signup, etc.
   */
  excludeRoutes?: string[];
  /**
   * Auto-show delay in milliseconds after page load
   * Set to 0 to disable auto-show
   */
  autoShowDelay?: number;
}

/**
 * Provider that manages the floating getting started guide across the application
 * Shows the guide on all pages (except excluded ones) until dismissed or completed
 */
export const FloatingGettingStartedProvider = ({
  children,
  excludeRoutes = ["/app/login", "/app/register", "/app/reset", "/login", "/signup", "/auth", "/onboarding"],
  autoShowDelay = 2000,
}: FloatingGettingStartedProviderProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  });
  const [isManuallyTriggered, setIsManuallyTriggered] = useState(false);
  
  const { data } = useGettingStartedData();
  const shouldShow = shouldShowGettingStarted(data);

  // Check if current route should be excluded
  const shouldShowOnCurrentRoute = () => {
    const currentPath = window.location.pathname;
    const isExcluded = excludeRoutes.some(route => {
      // Handle exact match for root path "/"
      if (route === "/") {
        return currentPath === "/";
      }
      // For other routes, check if current path starts with the route
      return currentPath.startsWith(route);
    });
    return !isExcluded;
  };

  useEffect(() => {
    // Don't show if dismissed, completed, or on excluded routes
    if (isDismissed || !shouldShow || !shouldShowOnCurrentRoute()) {
      setIsVisible(false);
      return;
    }

    // Auto-show with delay
    if (autoShowDelay > 0) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, autoShowDelay);

      return () => clearTimeout(timer);
    } else {
      setIsVisible(true);
    }
  }, [isDismissed, shouldShow, autoShowDelay]);

  // Listen for route changes to hide/show guide appropriately
  useEffect(() => {
    const handleRouteChange = () => {
      if (!shouldShowOnCurrentRoute()) {
        setIsVisible(false);
      } else if (!isDismissed && shouldShow && autoShowDelay === 0) {
        setIsVisible(true);
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", handleRouteChange);
    
    return () => {
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, [isDismissed, shouldShow, autoShowDelay]);

  const showGuide = () => {
    console.log('showGuide called with conditions:', {
      isDismissed,
      shouldShow,
      shouldShowOnCurrentRoute: shouldShowOnCurrentRoute(),
      currentPath: window.location.pathname,
      data
    });
    
    // Allow manual triggering to override dismissed state temporarily
    setIsManuallyTriggered(true);
    setIsVisible(true);
  };

  const hideGuide = () => {
    setIsVisible(false);
  };

  const dismissGuide = () => {
    setIsVisible(false);
    setIsManuallyTriggered(false);
    setIsDismissed(true);
    localStorage.setItem(DISMISSED_KEY, "true");
  };

  const handleMinimize = () => {
    setIsVisible(false);
    setIsManuallyTriggered(false);
    // Auto-show again after 30 seconds unless completed or permanently dismissed
    if (shouldShow && !isDismissed) {
      setTimeout(() => {
        const stillDismissed = localStorage.getItem(DISMISSED_KEY) === "true";
        if (!stillDismissed && shouldShowOnCurrentRoute()) {
          setIsVisible(true);
        }
      }, 30000);
    }
  };

  const contextValue: FloatingGettingStartedState = {
    isVisible,
    showGuide,
    hideGuide,
    dismissGuide,
  };

  return (
    <FloatingGettingStartedContext.Provider value={contextValue}>
      {children}
      {isVisible && shouldShowOnCurrentRoute() && (isManuallyTriggered || (!isDismissed && shouldShow)) && (
        <FloatingGettingStartedGuide 
          onDismiss={dismissGuide}
          onMinimize={handleMinimize}
        />
      )}
    </FloatingGettingStartedContext.Provider>
  );
};

/**
 * Hook to access the floating getting started guide state and controls
 */
export const useFloatingGettingStarted = () => {
  const context = useContext(FloatingGettingStartedContext);
  if (context === undefined) {
    throw new Error(
      "useFloatingGettingStarted must be used within a FloatingGettingStartedProvider"
    );
  }
  return context;
};

/**
 * Optional hook to manually trigger the guide (useful for help buttons, etc.)
 */
export const useShowGettingStarted = () => {
  const { showGuide } = useFloatingGettingStarted();
  return showGuide;
}; 